import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Spinner, Alert } from 'react-bootstrap';
import {
    BarChart, Bar, Line, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell
} from 'recharts';
import api from '../../../../services/api';
import { useLocation } from 'react-router-dom';
import AppTour from '../../../../components/common/AppTour';
import './Reports.css'; // We'll create this minimal CSS

const Reports = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const location = useLocation();
    const [runTour, setRunTour] = useState(false);
    const [theme, setTheme] = useState(() => localStorage.getItem('reports_theme') || 'idev');

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        localStorage.setItem('reports_theme', theme);
    }, [theme]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('tour') === 'reportes') {
            setRunTour(true);
        }
        fetchData();
    }, [location.search]);

    const fetchData = async () => {
        try {
            const response = await api.get('/reportes/');
            setData(response.data);
            setLoading(false);
        } catch (err) {
            console.error("Error cargando reportes:", err);
            setError("No se pudieron cargar los datos del reporte.");
            setLoading(false);
        }
    };

    const [downloadingExcel, setDownloadingExcel] = useState(false);
    const [downloadingPDF, setDownloadingPDF] = useState(false);

    const handleDownloadPDF = async () => {
        setDownloadingPDF(true);
        try {
            const response = await api.get('/reportes/download_pdf/', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '');
            link.setAttribute('download', `Reporte_Ejecutivo_${dateStr}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error("Error al descargar PDF:", err);
            if (err.response && err.response.data instanceof Blob) {
                const text = await err.response.data.text();
                try {
                    const errorJson = JSON.parse(text);
                    alert(`Error: ${errorJson.error || 'Error al descargar el reporte PDF'} `);
                } catch (e) {
                    alert("Error al descargar el reporte PDF.");
                }
            } else {
                alert("Error al conectar con el servidor para descargar el PDF.");
            }
        } finally {
            setDownloadingPDF(false);
        }
    };

    const handleDownloadExcel = async () => {
        setDownloadingExcel(true);
        try {
            const response = await api.get('/reportes/download_excel/', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '');
            link.setAttribute('download', `Reporte_Ejecutivo_${dateStr}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error("Error al descargar Excel:", err);
            if (err.response && err.response.data instanceof Blob) {
                const text = await err.response.data.text();
                try {
                    const errorJson = JSON.parse(text);
                    alert(`Error: ${errorJson.error || 'Error al descargar el reporte Excel'}`);
                } catch (e) {
                    alert("Error al descargar el reporte Excel.");
                }
            } else {
                alert("Error al descargar el reporte Excel. Verifique la conexión con el servidor.");
            }
        } finally {
            setDownloadingExcel(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    if (!data) return null;

    const COLORS_IDEV = {
        primary: '#6366f1',
        secondary: '#ec4899',
        accent: '#f59e0b',
        success: '#10b981',
        info: '#06b6d4',
        dark: '#1e293b'
    };

    const COLORS_EMPRESA = {
        primary: data?.colores_personalizados?.primario || '#D32F2F', // Default to a Red if not set, instead of orange
        secondary: data?.colores_personalizados?.secundario || '#212121', // Default to Black/Dark
        text: data?.colores_personalizados?.texto || '#212121',
        success: '#10b981',
        info: '#06b6d4'
    };

    const activeColors = theme === 'idev' ? COLORS_IDEV : COLORS_EMPRESA;

    // Map statuses to theme-aware colors
    const getStatusColor = (status, index) => {
        const statusColors = {
            'Aceptada': '#10b981',
            'Rechazada': '#ef4444',
            'Enviada': activeColors.primary,
            'Borrador': '#94a3b8'
        };
        return statusColors[status] || activeColors.primary;
    };



    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip" style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    padding: '10px 15px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}>
                    <p className="label mb-1 fw-bold text-dark">{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} className="mb-0" style={{ color: entry.color || activeColors.primary, fontSize: '0.9rem' }}>
                            {entry.name}: {entry.name === 'Monto ($)'
                                ? `$${entry.value.toLocaleString('es-CL')} `
                                : entry.value}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const reportesTourSteps = [
        {
            element: '#tour-reportes-header',
            intro: 'Tus Reportes y Estadísticas: Aquí el sistema suma todas tus ventas y te muestra de forma simple y en gráficas cómo le está yendo a tu negocio.',
            position: 'bottom',
        },
        {
            element: '#tour-reportes-descargas',
            intro: 'Descargar en Excel: ¿Necesitas hacer cuentas? Aquí puedes exportar toda la información de tus ventas.',
            position: 'left',
        },
        {
            element: '#tour-reportes-resumen',
            intro: 'Resumen Rápido: Echale un vistazo rápido al total que has cotizado, cuántas te aprobaron y el dinero que ingresará.',
            position: 'bottom',
        },
        {
            element: '#tour-reportes-graficos',
            intro: 'Gráficas de Ventas: Estos gráficos te ayudarán a entender qué meses han sido mejores para ti y cuántas ventas están a punto de cerrarse.',
            position: 'top',
        }
    ];

    return (
        <div className="container-fluid p-4">
            {/* 🌟 Capa del Tour Guiado Inyectada 🌟 */}
            {runTour && (
                <AppTour
                    steps={reportesTourSteps}
                    runTour={runTour}
                    setRunTour={setRunTour}
                    tourOptions={{ doneLabel: 'Continuar a Configuración' }}
                    onTourComplete={() => {
                        window.location.href = '/app/cotizaplus/configuracion?tour=configuracion_modulo';
                    }}
                />
            )}

            <div className="d-flex justify-content-between align-items-center mb-4" id="tour-reportes-header">
                <div>
                    <h2 className="fw-bold mb-0" style={{ color: activeColors.primary }}>Reportes de Análisis</h2>
                    <p className="text-secondary small">Visualiza el rendimiento de tu negocio</p>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <div className="theme-toggle-container bg-white p-1 rounded-pill d-flex shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
                        <div 
                            className={`rounded-circle d-flex align-items-center justify-content-center cursor-pointer`}
                            onClick={() => setTheme('idev')}
                            style={{ 
                                width: '32px', 
                                height: '32px', 
                                backgroundColor: theme === 'idev' ? '#f1f5f9' : 'transparent',
                                transition: 'all 0.2s',
                                cursor: 'pointer'
                            }}
                            title="idevplus Theme"
                        >
                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: COLORS_IDEV.primary, border: theme === 'idev' ? '2px solid white' : 'none', boxShadow: theme === 'idev' ? '0 0 0 2px #6366f1' : 'none' }}></div>
                        </div>
                        <div 
                            className={`rounded-circle d-flex align-items-center justify-content-center cursor-pointer`}
                            onClick={() => setTheme('empresa')}
                            style={{ 
                                width: '32px', 
                                height: '32px', 
                                backgroundColor: theme === 'empresa' ? '#f1f5f9' : 'transparent',
                                transition: 'all 0.2s',
                                cursor: 'pointer'
                            }}
                            title="Empresa Theme"
                        >
                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: COLORS_EMPRESA.primary, border: theme === 'empresa' ? '2px solid white' : 'none', boxShadow: theme === 'empresa' ? `0 0 0 2px ${COLORS_EMPRESA.primary}` : 'none' }}></div>
                        </div>
                    </div>
                    <div className="d-flex gap-2" id="tour-reportes-descargas">
                        <Button 
                            variant="outline-success" 
                            onClick={handleDownloadExcel}
                            disabled={downloadingExcel}
                            className="shadow-sm border-2 fw-bold"
                        >
                            {downloadingExcel ? (
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                            ) : (
                                <i className="bi bi-file-earmark-excel me-2"></i>
                            )}
                            {downloadingExcel ? 'Generando...' : 'Excel'}
                        </Button>
                        <Button 
                            variant="outline-danger" 
                            onClick={handleDownloadPDF}
                            disabled={downloadingPDF}
                            className="shadow-sm border-2 fw-bold"
                        >
                            {downloadingPDF ? (
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                            ) : (
                                <i className="bi bi-file-earmark-pdf me-2"></i>
                            )}
                            {downloadingPDF ? 'Generando...' : 'PDF'}
                        </Button>
                    </div>
                </div>
            </div>


            <h4 className="text-secondary fw-bold mb-3 mt-4">Análisis de cotizaciones</h4>
            {/* Resumen Cards */}
            <Row className="mb-4" id="tour-reportes-resumen">
                <Col md={4} className="mb-3 mb-md-0">
                    <Card className="shadow-sm border-0 h-100 text-white overflow-hidden" style={{ background: `linear-gradient(135deg, ${activeColors.primary} 0%, ${theme === 'idev' ? activeColors.secondary : activeColors.primary} 100%)` }}>
                        <Card.Body className="d-flex flex-column justify-content-center p-4">
                            <h6 className="opacity-75 mb-1 fw-bold">Total Cotizaciones</h6>
                            <h3 className="fw-bold display-5 mb-0">{data?.resumen?.total_cotizaciones || 0}</h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4} className="mb-3 mb-md-0">
                    <Card className="shadow-sm border-0 h-100 text-white overflow-hidden" style={{ background: `linear-gradient(135deg, #10b981 0%, ${theme === 'idev' ? '#059669' : activeColors.primary} 100%)` }}>
                        <Card.Body className="d-flex flex-column justify-content-center p-4">
                            <h6 className="opacity-75 mb-1 fw-bold">Tasa de Aprobación</h6>
                            <h3 className="fw-bold display-5 mb-0">{data?.resumen?.tasa_aprobacion || 0}%</h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="shadow-sm border-0 h-100 text-white overflow-hidden" style={{ background: `linear-gradient(135deg, #06b6d4 0%, ${theme === 'idev' ? '#0891b2' : activeColors.secondary} 100%)` }}>
                        <Card.Body className="d-flex flex-column justify-content-center p-4">
                            <h6 className="opacity-75 mb-1 fw-bold">Monto Total Aprobado</h6>
                            <h3 className="fw-bold display-5 mb-0">${(data?.resumen?.monto_total_aprobado || 0).toLocaleString('es-CL', { maximumFractionDigits: 0 })}</h3>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>



            <Row className="mb-4">
                <Col lg={4} className="mb-4">
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Header className="bg-white border-0 py-3">
                            <h5 className="mb-0 fw-bold" style={{ color: activeColors.primary }}>Distribución de Cotizaciones</h5>
                        </Card.Header>
                        <Card.Body style={{ height: '350px' }}>
                            {data.distribucion_estado ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={data.distribucion_estado}
                                            cx="50%"
                                            cy={isMobile ? '40%' : '50%'}
                                            innerRadius={isMobile ? 45 : 60}
                                            outerRadius={isMobile ? 80 : 100}
                                            paddingAngle={5}
                                            dataKey="value"
                                        >
                                            {data.distribucion_estado.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={getStatusColor(entry.name, index)} stroke="none" />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            contentStyle={{
                                                borderRadius: '8px',
                                                border: 'none',
                                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                            }}
                                        />
                                        <Legend
                                            layout={isMobile ? 'horizontal' : 'vertical'}
                                            verticalAlign={isMobile ? 'bottom' : 'middle'}
                                            align={isMobile ? 'center' : 'right'}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="text-center text-muted mt-5">No hay datos suficientes</div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={8} className="mb-4">
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Header className="bg-white border-0 py-3">
                            <h5 className="mb-0 fw-bold" style={{ color: activeColors.primary }}>Ventas Aprobadas por Mes</h5>
                        </Card.Header>
                        <Card.Body style={{ height: '400px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart
                                    data={data.ventas_mensuales}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                                >
                                    <defs>
                                        <linearGradient id="colorMonto" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={activeColors.primary} stopOpacity={0.8} />
                                            <stop offset="95%" stopColor={activeColors.primary} stopOpacity={0.1} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                                    <XAxis
                                        dataKey="mes"
                                        tick={{ fill: '#6b7280', fontSize: 12 }}
                                        tickLine={false}
                                        axisLine={{ stroke: '#e5e7eb' }}
                                        dy={10}
                                    />
                                    <YAxis
                                        yAxisId="left"
                                        orientation="left"
                                        tick={{ fill: '#6b7280', fontSize: 12 }}
                                        tickLine={false}
                                        axisLine={false}
                                        tickFormatter={(value) => `$${value / 1000} k`}
                                        label={{ value: 'Monto', angle: -90, position: 'insideLeft', fill: '#9ca3af', fontSize: 12 }}
                                    />
                                    <YAxis
                                        yAxisId="right"
                                        orientation="right"
                                        tick={{ fill: '#6b7280', fontSize: 12 }}
                                        tickLine={false}
                                        axisLine={false}
                                        label={{ value: 'Cant.', angle: 90, position: 'insideRight', fill: '#9ca3af', fontSize: 12 }}
                                    />
                                    <Tooltip
                                        content={<CustomTooltip />}
                                        cursor={{ fill: `${activeColors.primary}10` }} // 10% opacity hex
                                    />
                                    <Legend
                                        wrapperStyle={{ paddingTop: '20px' }}
                                        iconType="circle"
                                    />
                                    <Bar
                                        yAxisId="left"
                                        dataKey="total"
                                        name="Monto ($)"
                                        fill="url(#colorMonto)"
                                        barSize={40}
                                        radius={[8, 8, 0, 0]}
                                    />
                                    <Line
                                        yAxisId="right"
                                        type="monotone"
                                        dataKey="cantidad"
                                        name="Cantidad"
                                        stroke={activeColors.secondary}
                                        strokeWidth={3}
                                        dot={{ r: 4, fill: '#fff', stroke: activeColors.secondary, strokeWidth: 2 }}
                                        activeDot={{ r: 6, fill: activeColors.secondary }}
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Gráfico de Top Productos */}
            <Row className="mb-4">
                <Col lg={12} className="mb-4">
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Header className="bg-white border-0 py-3">
                            <h5 className="mb-0 fw-bold" style={{ color: activeColors.primary }}>Top 5 Productos/Servicios Más Solicitados</h5>
                        </Card.Header>
                        <Card.Body style={{ height: '400px' }}>
                            {data.top_productos && data.top_productos.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        layout="vertical"
                                        data={data.top_productos}
                                        margin={{ top: 20, right: 30, left: isMobile ? 10 : 100, bottom: 5 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                                        <XAxis type="number" />
                                        <YAxis dataKey="nombre" type="category" width={isMobile ? 80 : 150} tick={{ fontSize: isMobile ? 11 : 14 }} />
                                        <Tooltip
                                            cursor={{ fill: 'transparent' }}
                                            contentStyle={{
                                                borderRadius: '8px',
                                                border: 'none',
                                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                            }}
                                        />
                                        <Legend />
                                        <Bar dataKey="cantidad" name="Cantidad Solicitada" fill={activeColors.success} barSize={30} radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="text-center text-muted mt-5">No hay datos suficientes</div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Reports;
